import { CopyButton } from "~/components/CopyableField"
import { CodeBlock } from "~/components/ui/code-block"
import { ChevronRightIcon } from "lucide-react"
import type { ReactNode } from "react"
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger
} from "~/components/ui/collapsible"
import { Button } from "../ui/button"
import { FullScreen } from "./FullScreen"

export function AppErrorScreen({
	summary,
	errorId,
	details,
	footer
}: {
	summary: string
	errorId?: string | null
	details: { label: string; value: unknown }[]
	footer?: ReactNode
}) {
	const detailText = formatErrorDetails([
		{ label: "Captured at", value: new Date().toISOString() },
		{ label: "Path", value: window.location.href },
		...details,
		...(errorId ? [{ label: "Error report ID", value: errorId }] : [])
	])

	return (
		<FullScreen
			title="Unhandled App Error"
			contentClassName="w-[min(42rem,calc(100vw-2rem))]"
			message={
				<div className="flex w-full flex-col gap-4 text-left text-sm text-text-small">
					<div className="flex flex-col gap-2 text-center">
						<p>{summary}</p>
						<p>
							We captured a diagnostic report so the team can investigate this
							crash.
						</p>
						{errorId && (
							<p>
								Error report ID:{" "}
								<code className="rounded bg-background-tertiary px-1.5 py-0.5 font-mono text-text-sub">
									{errorId}
								</code>
							</p>
						)}
					</div>
					<Collapsible className="group w-full rounded-md border border-stroke bg-background/80 text-left">
						<div className="flex items-center gap-2 px-3 py-2">
							<CollapsibleTrigger className="flex min-w-0 flex-1 items-center text-left text-sm font-semibold text-text-main">
								Error details
							</CollapsibleTrigger>
							<CopyButton
								value={detailText}
								label="error details"
								size="icon-xs"
							/>
							<CollapsibleTrigger asChild>
								<Button
									variant="ghost"
									size="icon-xs"
									shape="pill"

									aria-label="Toggle error details"
								>
									<ChevronRightIcon className="size-4 text-icons transition-transform group-data-[state=open]:rotate-90" />
								</Button>
							</CollapsibleTrigger>
						</div>
						<CollapsibleContent>
							<CodeBlock>
								<code className="font-mono">{detailText}</code>
							</CodeBlock>
						</CollapsibleContent>
					</Collapsible>
					{footer}
				</div>
			}
		/>
	)
}

export function getErrorReportId(event: unknown) {
	return getStringProperty(event, "uuid")
}

export function describeUnknownError(error: unknown) {
	if (error instanceof Error) {
		return {
			name: error.name,
			message: error.message,
			stack: error.stack,
			cause: error.cause
		}
	}

	return normalizeValue(error)
}

function formatErrorDetails(details: { label: string; value: unknown }[]) {
	return details
		.map(({ label, value }) => `${label}:\n${stringifyDetail(value)}`)
		.join("\n\n")
}

function stringifyDetail(value: unknown) {
	if (typeof value === "string") return value

	try {
		return JSON.stringify(normalizeValue(value), null, 2)
	} catch {
		return String(value)
	}
}

function normalizeValue(value: unknown, seen = new WeakSet<object>()): unknown {
	if (value instanceof Error) return describeUnknownError(value)
	if (value === null || typeof value !== "object") return value

	if (seen.has(value)) return "[Circular]"
	seen.add(value)

	if (Array.isArray(value)) {
		return value.map((item) => normalizeValue(item, seen))
	}

	return Object.fromEntries(
		Object.entries(value).map(([key, entry]) => [
			key,
			normalizeValue(entry, seen)
		])
	)
}

function getStringProperty(value: unknown, property: string) {
	if (!value || typeof value !== "object" || !(property in value)) return null

	const propertyValue = value[property as keyof typeof value]
	return typeof propertyValue === "string" ? propertyValue : null
}
